import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Select,
  MenuItem,
  Alert,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import RoleCreation from "../role_creation";
import userApi from "../../../services/users";
import roleApi from "../../../services/role";
import menucreatedapi from "../../../services/role";
import { useDispatch, useSelector } from "react-redux";
import { showLoader, hideLoader } from "../../../redux/loader/actions";
import { showSnackbar } from "../../../redux/snackbar/actions";
import { encryptAES } from "../../../utils";

const useStyles = makeStyles({
  animatedButton: {
    background: "linear-gradient(45deg, #ff416c 30%, #ff4b2b 90%)",
    color: "#fff",
    padding: "10px 30px",
    fontSize: "16px",
    fontWeight: "bold",
    textTransform: "none",
    borderRadius: "5px",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      background: "linear-gradient(45deg, #ff4b2b 30%, #ff416c 90%)",
    },
  },
  chip: {
    margin: "4px",
  },
  buttonContainer: {
    position: "relative",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-start",
  },
  dialogTitleContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  closeButton: {
    marginLeft: "auto",
  },
});

const UserFormModal = ({
  open,
  onClose,
  onSave,
  existingUser,
  onEditUser,
  userDatabyID,
  mode,
  setMode,
  fetchUsers,
  setUserDatabyID,
}) => {
  const organizationItems = useSelector(
    (state) => state.organizationReducer.items
  );
  const classes = useStyles();
  const dispatch = useDispatch();
  const [customRoleModalOpen, setCustomRoleModalOpen] = useState(false);
  const [roleList, setRoleList] = useState([]);
  const [menulist, setMenuList] = useState([]);

  const handleClose = () => {
    formik.resetForm();
    onClose();
    setMode("");
    setUserDatabyID([]);
  };

  const getAllRoles = () => {
    dispatch(showLoader());

    const onSuccess = (res) => {
      dispatch(hideLoader());
      showSnackbar({
        message: res?.data?.msg,
        autoHideDuration: 3000,
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
        variant: "success",
      });
      setRoleList(res?.data?.data);
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      console.error("Failed to fetch data", err);
    };

    roleApi.getAllRole().then(onSuccess).catch(onFailure);
  };

  useEffect(() => {
    if (open) {
      getAllRoles();
    }
  }, [open, organizationItems]);

  const handleSubmit = useCallback(
    (values) => {
      if (values.isTagitstoreAdmin && !values.role) {
        dispatch(
          showSnackbar({
            message: "Please select role",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "warning",
          })
        );
        return;
      }
      if (!values.isTagitstoreAdmin && !values.isProductUser) {
        dispatch(
          showSnackbar({
            message: "Please select access type",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "warning",
          })
        );
        return;
      }
      dispatch(showLoader());
      let loginByField = "";

      if (values.emailCheckbox && values.email) {
        loginByField = "email";
      } else if (values.mobilenumberCheckbox && values.mobilenumber) {
        loginByField = "mobile_number";
      } else {
        console.error("Either email or mobile number must be selected");
        return;
      }

      const data = {
        username: values.name,
        email: values?.email?.toLowerCase(),
        mobile_number: values.mobilenumber,
        tagitstore_access: values.isTagitstoreAdmin,
        product_access: values.isProductUser,
        login_by: loginByField,
        ...(values.isTagitstoreAdmin && { role_uuid: values.role }),
      };

      const encryptedData = encryptAES(data);
      const onSuccess = (res) => {
        dispatch(hideLoader());

        if (res.data.status === true) {
          dispatch(
            showSnackbar({
              message: res?.data?.msg || "Info updated successfully",
              autoHideDuration: 3000,
              anchorOrigin: {
                vertical: "top",
                horizontal: "right",
              },
              variant: "success",
            })
          );
          handleClose();
          formik.resetForm();
          fetchUsers();
        }
      };

      const onFailure = (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.msg,
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "error",
          })
        );
      };
      if (mode === "edit") {
        userApi
          .userEdit(userDatabyID?.uuid, encryptedData)
          .then(onSuccess)
          .catch(onFailure);
      } else {
        userApi.userCreate(encryptedData).then(onSuccess).catch(onFailure);
      }
    },
    [dispatch, handleClose]
  );

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      mobilenumber: "",
      isTagitstoreAdmin: false,
      isProductUser: false,
      emailCheckbox: false,
      mobilenumberCheckbox: false,
      role: "",
    },
    validate: (values) => {
      const errors = {};
      if (!values.name) {
        errors.name = "Name is required";
      }
      if (values.isTagitstoreAdmin && !values.role) {
        errors.role = "Role is required when Tagitstore BOC is checked";
      }
      if (values.email) {
        if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
          errors.email = "Invalid email address";
        }
      }
      if (values.mobilenumber) {
        if (!/^\d{10}$/.test(values.mobilenumber)) {
          errors.mobilenumber = "Mobile number must be exactly 10 digits";
        }
        if (!values.mobilenumber) {
          errors.mobilenumber = "Mobile number is required";
        }
      }
      if (values.emailCheckbox) {
        if (!values.email) {
          errors.email = "Email is required";
        } else if (
          !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
        ) {
          errors.email = "Invalid email address";
        }
      }
      if (
        values.isProductUser &&
        !values.emailCheckbox &&
        !values.mobilenumberCheckbox
      ) {
        errors.emailCheckbox = "Either Email or Mobile Number must be selected";
        errors.mobilenumberCheckbox =
          "Either Email or Mobile Number must be selected";
      }
      if (values.isProductUser) {
        if (values.emailCheckbox) {
          if (!values.email) {
            errors.email = "Email is required";
          } else if (
            !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
          ) {
            errors.email = "Invalid email address";
          }
        }
        if (!values.mobilenumber) {
          errors.mobilenumber = "Mobile number is required";
        }
      }

      return errors;
    },
    onSubmit: (values, errors) => {
      if (errors.role) {
        alert("Fill data");
      }
      handleSubmit(values);
    },
  });

  const [isDisablemail, setIsDisablemail] = useState();
  const [editDisable, setEditDisable] = useState(false);
  const [isDisablemobile, setIsDisablemobile] = useState();
  useEffect(() => {
    if (formik.values.isTagitstoreAdmin && formik.values.isProductUser) {
      // if (mode !== "edit") {
      formik.setFieldValue("emailCheckbox", true);
      formik.setFieldValue("mobilenumberCheckbox", false);
      setIsDisablemail(true);
      setIsDisablemobile(true);
      // }
    } else if (formik.values.isTagitstoreAdmin) {
      // if (mode !== "edit") {
      formik.setFieldValue("emailCheckbox", true);
      formik.setFieldValue("mobilenumberCheckbox", false);
      setIsDisablemail(true);
      setIsDisablemobile(true);
      // }
    } else if (formik.values.isProductUser) {
      // if (mode !== "edit") {
      // formik.setFieldValue("emailCheckbox", false);
      formik.setFieldValue("emailCheckbox", true);
      formik.setFieldValue("mobilenumberCheckbox", false);
      setIsDisablemail(false);
      setIsDisablemobile(false);
      // }
    } else {
      // if (mode !== "edit") {
      // formik.setFieldValue("emailCheckbox", false);
      formik.setFieldValue("emailCheckbox", true);
      formik.setFieldValue("mobilenumberCheckbox", false);
      setIsDisablemail(true);
      setIsDisablemobile(true);
      // }
    }
  }, [
    formik.values.isTagitstoreAdmin,
    formik.values.isProductUser,
    organizationItems,
    mode,
  ]);

  const handleRoleChange = (event) => {
    const selectedRole = event.target.value;
    if (selectedRole === "custom") {
      setCustomRoleModalOpen(true);
    } else {
      formik.setFieldValue("role", selectedRole);
    }
  };

  const handleProductUserChange = (e) => {
    formik.setFieldValue("isProductUser", e.target.checked);

    if (e.target.checked) {
      formik.setFieldValue("emailCheckbox", false);
      formik.setFieldValue("mobilenumberCheckbox", true);
    } else {
      formik.setFieldValue("emailCheckbox", true);
      formik.setFieldValue("mobilenumberCheckbox", false);
    }
  };

  useEffect(() => {
    if (mode === "edit" && userDatabyID) {
      setEditDisable(true);
      const email =
        userDatabyID?.bocUserInfo?.[0]?.login_by !== "mobile_number";
      const mobile = userDatabyID?.bocUserInfo?.[0]?.login_by !== "email";
      formik.setFieldValue("name", userDatabyID.username);
      formik.setFieldValue("email", userDatabyID.email);
      formik.setFieldValue("mobilenumber", userDatabyID.mobile_number);
      formik.setFieldValue(
        "isTagitstoreAdmin",
        userDatabyID?.bocUserInfo[0]?.tagitstore_access
      );
      formik.setFieldValue(
        "isProductUser",
        userDatabyID?.bocUserInfo[0]?.product_access
      );
      formik.setFieldValue("emailCheckbox", email);

      formik.setFieldValue("mobilenumberCheckbox", mobile);

      formik.setFieldValue(
        "role",
        userDatabyID && userDatabyID?.roleMappings[0]?.role?.uuid
      );
    }
  }, [mode, userDatabyID, editDisable, organizationItems]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle disableTypography className={classes.dialogTitleContainer}>
        {mode === "edit" ? "Edit User" : "Add User"}
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
          className={classes.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <Typography variant="subtitle2">Access For</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.isTagitstoreAdmin}
                  onChange={(e) =>
                    formik.setFieldValue("isTagitstoreAdmin", e.target.checked)
                  }
                  name="isTagitstoreAdmin"
                />
              }
              label="Tagitstore BOC"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.isProductUser}
                  onChange={handleProductUserChange}
                  name="isProductUser"
                />
              }
              label="Products"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              label="Name"
              variant="outlined"
              fullWidth
              value={formik.values.name}
              onChange={(e) => formik.setFieldValue("name", e.target.value)}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              label="Email"
              variant="outlined"
              fullWidth
              value={formik.values.email}
              onChange={(e) => {
                formik.setFieldValue("email", e.target.value);
                formik.setFieldTouched("email", true);
                formik.validateField("email");
              }}
              disabled={mode === "edit"}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Checkbox
                      checked={formik.values.emailCheckbox}
                      onChange={(e) => {
                        formik.setFieldValue("emailCheckbox", e.target.checked);
                        formik.setFieldValue("mobilenumberCheckbox", false);
                      }}
                      color="primary"

                      disabled="true"
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              label="Mobile Number"
              variant="outlined"
              fullWidth
              value={formik.values.mobilenumber}
              onChange={(e) =>
                formik.setFieldValue("mobilenumber", e.target.value)
              }
              error={
                formik.touched.mobilenumber &&
                Boolean(formik.errors.mobilenumber)
              }
              helperText={
                formik.touched.mobilenumber && formik.errors.mobilenumber
              }

            />
          </Grid>
          {formik.values.isTagitstoreAdmin && (
            <Grid item xs={12} sm={12} md={4}>
              <Select
                fullWidth
                value={formik.values.role}
                onChange={handleRoleChange}
                displayEmpty
                error={formik.touched.role && Boolean(formik.errors.role)}
              >
                <MenuItem value="" disabled>
                  Select Role
                </MenuItem>
                {Array.isArray(roleList) &&
                  roleList.map((data, index) => (
                    <MenuItem
                      key={index}
                      value={data?.uuid}
                      disabled={!data.is_menu_created}
                    >
                      {data?.role.charAt(0).toUpperCase() + data?.role.slice(1)}
                    </MenuItem>
                  ))}
              </Select>
              {formik.touched.role ||
                (formik.errors.role && (
                  <div
                    style={{
                      color: "#d33131",
                      fontSize: "13px",
                      paddingTop: "7px",
                      paddingLeft: "8px",
                    }}
                  >
                    {formik.errors.role}
                  </div>
                ))}
            </Grid>
          )}

          <Grid
            item
            xs={12}
            container
            justifyContent="flex-end"
            alignItems="center"
          >
            <Button
              type="submit"
              variant="contained"
              className={classes.animatedButton}
              onClick={formik.handleSubmit}
            >
              {mode === "edit" ? "Update" : "Add"}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions />
      <RoleCreation
        open={customRoleModalOpen}
        onClose={() => setCustomRoleModalOpen(false)}
        onSave={(roleName) => {
          formik.setFieldValue("role", roleName);
          setCustomRoleModalOpen(false);
        }}
      />
    </Dialog>
  );
};

export default UserFormModal;
